import { CommonModule, DOCUMENT } from '@angular/common';
import type { OnInit } from '@angular/core';
import { Component, DestroyRef, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { ToastContainerComponent } from '@hunting-nature/Secacam/shared/components/toast/toast-container.component';
import { executeAfterTranslationsLoad } from '@hunting-nature/shared-core/utils/execute-after-translations-load';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { filter, tap } from 'rxjs';

import { GeneralActions } from './shared/redux/general/general.actions';
import { GeneralSelectors } from './shared/redux/general/general.reducer';
import { HuntingProductsActions } from './pages/hunting-products/redux/hunting-products.actions';
import { PushNotificationService } from './shared/services/push-notification.service';
import { ConsentModalComponent } from './pages/menu/consent-modal/consent-modal.component';
import { WhatsNewComponent } from './pages/whats-new/whats-new.component';
import { CannesWifiConnectSheetComponent } from './pages/hunting-products/components/cannes-wifi-connect-sheet/cannes-wifi-connect-sheet.component';
import { AuthenticationService } from './shared/services/authentication.service';
import { BleDeviceManagerService } from './shared/services/BLE/ble-device-manager.service';

@Component({
  standalone: true,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [IonRouterOutlet, IonApp, CommonModule, ConsentModalComponent, WhatsNewComponent, CannesWifiConnectSheetComponent, ToastContainerComponent],
})
export class AppComponent implements OnInit {
  // services
  private readonly store = inject(Store);
  private readonly authService = inject(AuthenticationService);
  private readonly pushNotificationService = inject(PushNotificationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly translocoService: TranslocoService = inject(TranslocoService);
  private readonly bleDeviceManager = inject(BleDeviceManagerService);

  // private properties
  private readonly activeAppLanguage$$ = this.store.selectSignal(GeneralSelectors.activeAppLanguage);

  // UI bindings
  readonly useDarkMode$$ = this.store.selectSignal(GeneralSelectors.isUseDarkMode);
  readonly isOfflineBannerVisible$$ = this.store.selectSignal(GeneralSelectors.isOfflineBannerVisible);
  protected readonly isBleFeatureEnabled = FEATURE_BLE;

  constructor() {
    this.syncRootTheme();
    executeAfterTranslationsLoad(() => this.setHtmlLanguage());
  }

  ngOnInit(): void {
    // TODO: combined them all to appInitialized action or move it to initApp$ method of general Service
    this.store.dispatch(GeneralActions.initAppTranslation());
    this.store.dispatch(GeneralActions.initAppSettings());
    this.initPushNotifications();
    this.initAuthentication();
    this.initAppListeners();
    this.initBleDisconnectListener();
  }

  /**
   * Listen for unexpected BLE disconnects and dispatch connectionLost action.
   * This enables auto-reconnect recovery (AC 10).
   */
  private initBleDisconnectListener(): void {
    this.bleDeviceManager.onUnexpectedDisconnect$
      .pipe(
        filter(() => Capacitor.isNativePlatform()), // Only on native platforms
        tap(({ deviceId, timestamp }) => {
          this.store.dispatch(HuntingProductsActions.connectionLost({ deviceId, timestamp }));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private initPushNotifications(): void {
    void this.pushNotificationService.prepare().catch(error => {
      console.error('[AppComponent] prepare push listeners failed', error);
    });
  }

  private initAuthentication(): void {
    // The startup OIDC check already ran in the app initializer; dispatch its result now that
    // effects are subscribed so downstream login effects (e.g. what's-new) still run.
    const result = this.authService.startupAuthResult;
    if (!result) {
      return;
    }
    if ('loginResponse' in result) {
      this.store.dispatch(GeneralActions.loginSucceeded({ loginResponse: result.loginResponse, isLoginCallback: result.isLoginCallback }));
    } else {
      this.store.dispatch(GeneralActions.loginFailed({ error: result.error }));
    }
  }

  private initAppListeners(): void {
    void App.addListener('appUrlOpen', (event: { url: string }) => {
      this.dispatchCheckLoggedIn(event.url);
      Browser.close().catch(() => {});
    });

    void App.addListener('resume', () => {
      this.store.dispatch(GeneralActions.appResumed({ language: this.activeAppLanguage$$() }));
    });

    const cleanup = this.authService.initCrossTabLogoutListener(() => {
      this.store.dispatch(GeneralActions.crossTabLogoutDetected());
    });
    this.destroyRef.onDestroy(cleanup);
  }

  private dispatchCheckLoggedIn(url: string | undefined): void {
    const { url: parsedUrl, isLoginCallback } = url ? this.authService.parseOidcCallbackUrl(url) : { url: undefined, isLoginCallback: false };
    this.authService.loginCallbackInProgress = isLoginCallback;
    this.store.dispatch(GeneralActions.checkLoggedIn({ url: parsedUrl, isLoginCallback }));
  }

  private syncRootTheme(): void {
    effect(() => {
      const useDarkMode = this.useDarkMode$$();
      const rootElement = this.document.documentElement;

      rootElement.classList.toggle('ion-palette-dark', useDarkMode);
      rootElement.classList.toggle('theme-dark', useDarkMode);
      rootElement.classList.toggle('theme-light', !useDarkMode);
      rootElement.style.colorScheme = useDarkMode ? 'dark' : 'light';
    });
  }

  private setHtmlLanguage(): void {
    this.document.documentElement.lang = this.translocoService.getActiveLang();
  }
}
